//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { SocialNetwork } from '@models/SocialNetwork';
import { isDefined } from '@utils/filterPredicates';
import { escapeRegexSpecialChars } from '@utils/regexUtils';
import { parseHashtags, parseSocialText } from '@utils/stringUtils';

// https://support.tiktok.com/en/getting-started/setting-up-your-profile/changing-your-username
const tiktokMentionRegex = /^@[a-zA-Z0-9_.]+[a-zA-Z0-9_]$/;
// Detects the English "tag us" phrase as a fallback
const tiktokTagUs = /\btag\sus\b/i;
// CJK scripts don't delimit words with spaces, so a letter-adjacency boundary
// check would reject valid matches when the phrase abuts other CJK characters.
const cjkScriptPattern =
  /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Hangul}/u;

/**
 * Takes a full hashtag and returns the tag without the hash symbol
 *
 * @returns string The raw hastag without the hash symbol
 */
export function getHashtagWithoutHashSymbol(hashtag: string): string {
  return hashtag.replace('#', '');
}

//Addition to Reach tooltip content for IG and FB
export const getReachTooltipContentCaveat = (network: SocialNetwork) =>
  `${network} has changed how they report this metric in the past. You may see some differences in how this metric is calculated over time and in the future.`;

export function isValidUsername(
  username: string,
  network: Extract<SocialNetwork, 'Instagram' | 'TikTok' | 'YouTube'>,
  shouldPrependAt: boolean = true, // passing false in case of directUserNameSearch since "@" is expected to come appended
): boolean {
  const formattedUsername = shouldPrependAt
    ? `${username.startsWith('@') ? '' : '@'}${username}`
    : username;
  if (network === SocialNetwork.Instagram) {
    const parsedUsername = parseSocialText(formattedUsername);
    return parsedUsername.length === 1 && parsedUsername[0]?.type === 'mention';
  }
  return tiktokMentionRegex.test(formattedUsername);
}

export const hasHashtag = (content?: string) => {
  return isDefined(content) && parseHashtags(content).length > 0;
};

export const hasTagUsPhrase = (content?: string, phrases?: string[]) => {
  if (!isDefined(content)) {
    return false;
  }
  const hasEnglishTagUs = tiktokTagUs.test(content);
  if (isDefined(phrases)) {
    return (
      phrases.some((phrase) => {
        const trimmedPhrase = phrase.trim();
        if (!trimmedPhrase) {
          return false;
        }
        if (cjkScriptPattern.test(trimmedPhrase)) {
          return content.includes(trimmedPhrase);
        }
        const escaped = escapeRegexSpecialChars(trimmedPhrase);
        return new RegExp(
          `(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`,
          'ui',
        ).test(content);
      }) || hasEnglishTagUs
    );
  }
  return hasEnglishTagUs;
};
